import React, { createContext, useContext, useState, useEffect } from "react";
import { getApps, initializeApp } from "firebase/app";
import { getMessaging, onMessage, isSupported } from "firebase/messaging";
import { toast } from "react-toastify";

import {
  initializerFirebase,
  getCurrentFCMToken,
} from "../common/firebaseHelpers";
import {
  checkBrowserSupport,
  checkSystemNotificationSettings,
} from "../common/serviceWorkerHelper";
import protectedAxios from "../common/axios";
import { API_URL } from "../common/constants";
import Utils from "../common/utils";
import CustomChatNotification from "../notifications/components/CustomChatNotification";

const PushNotificationContext = createContext();

export const usePushNotification = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error(
      "usePushNotification must be used within a PushNotificationProvider"
    );
  }
  return context;
};

const openNotification = (title, body, icon, clickAction) => {
  toast(
    <CustomChatNotification
      title={title}
      body={body}
      link={clickAction}
      avatarSrc={icon}
    />,
    {
      toastId: clickAction,
    }
  );
};

export const PushNotificationProvider = ({ children, loggedInUserId }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [notificationSupported, setNotificationSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState("default");

  // Fetch devices from backend
  const fetchDevices = async () => {
    if (!loggedInUserId) {
      setDevices([]);
      setDevicesLoading(false);
      return;
    }

    setDevicesLoading(true);
    try {
      const response = await protectedAxios.get(
        `${API_URL}/v1/notification/devices`
      );
      if (response.data.success) {
        setDevices(response.data.devices || []);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  // Check notification status
  const checkNotificationStatus = async () => {
    try {
      const supported = await isSupported();
      setNotificationSupported(supported);

      if (supported && typeof Notification !== "undefined") {
        setPermissionStatus(Notification.permission);

        if (loggedInUserId && Notification.permission === "granted") {
          const currentToken = await getCurrentFCMToken();

          if (currentToken) {
            const currentInstanceId = currentToken.split(":")[0];
            const currentDeviceExists = devices.some(
              (device) => device.instanceId === currentInstanceId
            );
            setIsSubscribed(currentDeviceExists);
          } else {
            setIsSubscribed(false);
          }
        } else {
          setIsSubscribed(false);
        }
      }
    } catch (error) {
      console.error("Error checking notification status:", error);
      setIsSubscribed(false);
    }
  };

  // Subscribe to notifications with enhanced error handling
  const subscribeToNotifications = async () => {
    try {
      // First check browser support
      const browserSupport = checkBrowserSupport();
      if (!browserSupport.supported) {
        const missingFeatures = [];
        if (!browserSupport.hasServiceWorker)
          missingFeatures.push("Service Workers");
        if (!browserSupport.hasNotifications)
          missingFeatures.push("Notifications API");
        if (!browserSupport.hasPushManager) missingFeatures.push("Push API");

        throw new Error(
          `Browser missing required features: ${missingFeatures.join(", ")}`
        );
      }

      // Check system notification settings
      const systemSettings = await checkSystemNotificationSettings();
      if (systemSettings.available && systemSettings.state === "denied") {
        throw new Error(
          "Notifications are blocked in system settings. Please enable them in your browser settings."
        );
      }

      const success = await initializerFirebase();
      if (success) {
        await fetchDevices(); // Refresh devices after subscription
        await checkNotificationStatus();

        // Send welcome notification
        try {
          await protectedAxios.post(`${API_URL}/v1/notification/test-push`);
        } catch (testError) {
          console.warn(
            "Welcome notification failed, but subscription succeeded:",
            testError
          );
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error subscribing to notifications:", error);

      // Provide user-friendly error messages
      let errorMessage = "Failed to enable notifications. ";

      if (error.message?.includes("push service error")) {
        errorMessage +=
          "Push service is temporarily unavailable. Please try again later.";
      } else if (error.message?.includes("missing required features")) {
        errorMessage += error.message;
      } else if (error.message?.includes("blocked in system settings")) {
        errorMessage += error.message;
      } else if (error.message?.includes("permission-blocked")) {
        errorMessage +=
          "Notification permission was denied. Please enable it in your browser settings.";
      } else {
        errorMessage += "Please check your browser settings and try again.";
      }

      // You might want to show this error to the user
      console.error(errorMessage);

      return false;
    }
  };

  // Send test notification to specific device
  const sendTestToDevice = async (instanceId) => {
    try {
      await protectedAxios.post(
        `${API_URL}/v1/notification/test-device/${instanceId}`
      );
      return true;
    } catch (error) {
      console.error("Error sending test notification:", error);
      return false;
    }
  };

  // Remove device
  const removeDevice = async (instanceId) => {
    try {
      await protectedAxios.delete(
        `${API_URL}/v1/notification/device/${instanceId}`
      );

      // Update local state
      setDevices(devices.filter((d) => d.instanceId !== instanceId));

      // Recheck subscription status
      await checkNotificationStatus();

      return true;
    } catch (error) {
      console.error("Error removing device:", error);
      return false;
    }
  };

  // Initialize Firebase messaging when user is logged in
  useEffect(() => {
    if (loggedInUserId) {
      (async function () {
        try {
          const firebaseInitialized = await initializerFirebase(false);

          if (firebaseInitialized && getApps().length > 0) {
            const messaging = await getMessaging(getApps()[0]);

            onMessage(messaging, (payload) => {
              const { title, body, clickAction, userId } =
                Utils.createNotificationContent(payload);

              if (!userId || userId !== loggedInUserId) return;

              const icon = payload.data.icon;
              const conversationId = Utils.getConversationId(clickAction);
              if (window.location.pathname !== conversationId) {
                if (toast.isActive(clickAction)) {
                  toast.update(clickAction, {
                    render: (
                      <CustomChatNotification
                        title={title}
                        body={body}
                        link={clickAction}
                        avatarSrc={icon}
                      />
                    ),
                  });
                } else {
                  openNotification(title, body, icon, clickAction);
                }
              }
            });
          }
        } catch (error) {
          // Don't report to Sentry if it's just unsupported browser
          if (!error.message?.includes("unsupported-browser")) {
            console.error("Firebase messaging setup error:", error);
          }
        }
      })();
    }
  }, [loggedInUserId]);

  // Fetch devices when user changes or component mounts
  useEffect(() => {
    fetchDevices();
  }, [loggedInUserId]);

  // Check notification status when devices change
  useEffect(() => {
    if (!devicesLoading) {
      checkNotificationStatus();
    }
  }, [devices, devicesLoading, loggedInUserId]);

  const contextValue = {
    // State
    isSubscribed,
    devices,
    devicesLoading,
    notificationSupported,
    permissionStatus,

    // Actions
    subscribeToNotifications,
    sendTestToDevice,
    removeDevice,
    fetchDevices,
    checkNotificationStatus,
  };

  return (
    <PushNotificationContext.Provider value={contextValue}>
      {children}
    </PushNotificationContext.Provider>
  );
};
